import { useEffect, useState } from "react";
import StatusPill from "shared/StatusPill";
import { cn } from "../../../lib/utils";
import { getRfpTimeline, type EmailStepContent, type LinkedinStepContent, type RfpTimeline, type TimelineCompany, type TimelineStep } from "../api/OutreachTouchesApi";
import { formatCalendarDate } from "../outreachDates";

const STATE_LABEL: Record<TimelineStep["state"], string> = { done: "done", planned: "planned", skipped: "skipped", stopped: "stopped" };

/** The date part of a step line; a stopped or skipped step with no date says nothing (it never will have one). */
function dateText(s: TimelineStep, drafted: boolean): string {
  if (s.date) return formatCalendarDate(s.date);
  if (s.state !== "planned") return "";
  if (s.display === "waiting_acceptance") return "dated after the request is accepted";
  if (s.display === "waiting_message") return "dated after the message is sent";
  return drafted ? "no date until the campaign starts" : "not dated";
}

function stepNote(s: TimelineStep): string {
  if (s.display === "awaiting_acceptance") return s.close_on ? `awaiting acceptance, closes ${formatCalendarDate(s.close_on)}` : "awaiting acceptance";
  if (s.state === "done") return s.computed ? "by the schedule" : s.accepted_on ? `accepted ${formatCalendarDate(s.accepted_on)}` : "";
  return s.reason ?? "";
}

/** The content of one step: the email as drafted, or the LinkedIn profile to open and the note to paste. */
function StepContent({ step }: { step: TimelineStep }) {
  const c = step.content;
  if (step.kind === "email") {
    const e = c as EmailStepContent | null | undefined;
    if (!e) return <p className="text-xs text-muted-foreground">No email text is on record for this company: the campaign was drafted before texts were recorded, or this company was not on its final lead list.</p>;
    return (
      <div className="space-y-1" data-testid={`content-${step.key}`}>
        {(e.email || e.to) && <p className="text-xs text-muted-foreground">To: {[e.to, e.email && `<${e.email}>`].filter(Boolean).join(" ")}</p>}
        {e.subject ? <p className="text-xs"><span className="text-muted-foreground">Subject:</span> {e.subject}</p> : <p className="text-xs text-muted-foreground">Same thread as email 1 (no subject).</p>}
        <pre className="whitespace-pre-wrap break-words font-sans text-xs bg-surface rounded-md border border-border p-2 max-h-72 overflow-auto">{e.body}</pre>
        {!e.personalised && <p className="text-xs text-muted-foreground">Greeting and fit line were not recorded; "there" and a blank fit line are shown.</p>}
      </div>
    );
  }
  const l = c as LinkedinStepContent | null | undefined;
  if (!l) return null;
  return (
    <div className="space-y-1" data-testid={`content-${step.key}`}>
      <p className="text-xs">
        <span className="text-muted-foreground">Contact:</span> {l.to ?? "no person chosen yet"}{l.title && `, ${l.title}`}
        {l.profile_url
          ? <> · <a href={l.profile_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">Open LinkedIn profile</a></>
          : <span className="text-muted-foreground"> · {l.needs_person ? "needs a person" : "no profile URL yet, add it on the contact"}</span>}
      </p>
      {l.text
        ? <><pre className="whitespace-pre-wrap break-words font-sans text-xs bg-surface rounded-md border border-border p-2">{l.text}</pre><p className="text-xs text-muted-foreground">{l.chars} characters</p></>
        : <p className="text-xs text-muted-foreground">No text was drafted for this step.</p>}
    </div>
  );
}

/** One company's steps: email 1, request, email 2, message, email 3, follow-up, in date order. Read-only. */
export function CompanyTimeline({ company, drafted }: { company: TimelineCompany; drafted: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div data-testid={`timeline-${company.prime_company_id}`}>
      <ol className="space-y-1.5">
        {company.steps.map((s) => {
          const muted = s.state === "planned" && !s.next;
          const blurred = s.state === "stopped" || s.state === "skipped";
          return (
            <li key={s.key} className={cn("flex items-start gap-3 text-sm", muted && "text-muted-foreground", blurred && "text-muted-foreground opacity-60", s.next && "font-medium text-foreground")}
                aria-current={s.next ? "step" : undefined}>
              <span className={cn("mt-1.5 w-2 h-2 rounded-full shrink-0", s.state === "done" ? "bg-success" : s.next ? "bg-primary" : "bg-border")} aria-hidden />
              <div className="min-w-0 flex-1">
                {s.label}
                {s.kind === "email" && <span className="text-xs text-muted-foreground"> (email)</span>}
                {dateText(s, drafted) && <span className="text-xs text-muted-foreground"> · {dateText(s, drafted)}</span>}
                {stepNote(s) && <span className="text-xs text-muted-foreground"> · {stepNote(s)}</span>}
                {open === s.key && <div className="mt-1.5 font-normal text-foreground"><StepContent step={s} /></div>}
              </div>
              {s.content !== undefined && (
                <button type="button" className="text-xs text-primary underline shrink-0" aria-expanded={open === s.key}
                        onClick={() => setOpen(open === s.key ? null : s.key)}>{open === s.key ? "Hide" : "View"}</button>
              )}
              {s.next ? <StatusPill tone="brand" size="sm">next</StatusPill> : <StatusPill tone={s.state === "done" ? "success" : "neutral"} size="sm">{STATE_LABEL[s.state]}</StatusPill>}
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground mt-2">
        {company.locked_until
          ? `Locked against other campaigns until ${formatCalendarDate(company.locked_until)} (email end ${company.email_floor ? formatCalendarDate(company.email_floor, false) : "—"}, plus LinkedIn touches, plus 7 days).`
          : "Not locked."}
        {" "}Email steps show the schedule, not proof of sending.
      </p>
    </div>
  );
}

export function useTimeline(opportunityId: number, companyId?: number) {
  const [data, setData] = useState<RfpTimeline | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    getRfpTimeline(opportunityId, companyId).then((d) => alive && setData(d)).catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [opportunityId, companyId]);
  return { data, failed };
}

/** RFP detail modal: every company of the campaign, each with its timeline. Nothing is shown before an outreach draft exists. */
export function RfpOutreachTimelines({ opportunityId, drafted }: { opportunityId: number; drafted: boolean }) {
  const { data, failed } = useTimeline(opportunityId);
  if (!drafted) return null;
  if (failed) return <p className="text-xs text-muted-foreground">Could not load the outreach timeline.</p>;
  if (!data) return <p className="text-xs text-muted-foreground">Loading the outreach timeline…</p>;
  if (data.companies.length === 0) return <p className="text-xs text-muted-foreground">No companies on this campaign yet.</p>;
  return (
    <div className="space-y-2" data-testid="rfp-timelines">
      {data.companies.map((c) => (
        <details key={c.prime_company_id} className="rounded-lg border border-border bg-surface-muted/40 px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            {c.company_name ?? `Company ${c.prime_company_id}`}
            {(c.contact_name || c.contact_email) && <span className="ml-2 font-normal text-muted-foreground">· {c.contact_name ?? c.contact_email}</span>}
            {c.replied && <span className="ml-2"><StatusPill tone="success" size="sm">replied</StatusPill></span>}
          </summary>
          <div className="mt-2"><CompanyTimeline company={c} drafted={data.drafted} /></div>
        </details>
      ))}
    </div>
  );
}

/** Prime contact profile page: one timeline under one RFP appearance. */
export function AppearanceTimeline({ opportunityId, companyId }: { opportunityId: number; companyId: number | null }) {
  const { data } = useTimeline(opportunityId, companyId ?? undefined);
  const company = data?.companies.find((c) => c.prime_company_id === companyId);
  if (!companyId || !data || !data.drafted || !company) return null;
  return (
    <details className="mt-2" data-testid={`appearance-timeline-${opportunityId}`}>
      <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Outreach timeline</summary>
      <div className="mt-2"><CompanyTimeline company={company} drafted={data.drafted} /></div>
    </details>
  );
}
